// Renders a result ticket = the score's app-asset PNG (all copy baked in, number areas
// empty) + the two live things drawn on top: the ticket number and the five stat numbers.
// Placement is expressed as fractions of the ticket's own width/height and calibrated to the
// `-example` reference PNGs. All live numbers are Ancizar Serif, black (spec §ticket anatomy).
import { ROUND } from '../game/config';

export type TicketVariant = 'intern' | 'wrangler' | 'crusher' | 'samurai' | 'senior' | 'zen';

// Universal play-counter number: zero-padded to 6 digits, or the dash fallback on error.
export const NO_FALLBACK = '——————';
export function padShiftNo(n: number): string {
  return String(Math.max(0, Math.floor(n))).padStart(6, '0');
}

export function variantForScore(score: number): TicketVariant {
  if (score < 100) return 'intern';
  if (score < 250) return 'wrangler';
  if (score < 450) return 'crusher';
  if (score < 700) return 'samurai';
  if (score < 1000) return 'senior';
  return 'zen';
}

const cache = new Map<TicketVariant, HTMLImageElement>();
function loadTicket(v: TicketVariant): Promise<HTMLImageElement> {
  const hit = cache.get(v);
  if (hit && hit.complete && hit.naturalWidth) return Promise.resolve(hit);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      cache.set(v, img);
      resolve(img);
    };
    img.onerror = reject;
    img.src = import.meta.env.BASE_URL + 'assets/result-' + v + '.png';
  });
}

// Live-number layout, as fractions of the ticket W/H. Calibrated against the -example PNGs.
const L = {
  numLeftX: 0.216, // ticket number: left edge, right of baked "No."
  numBaseY: 0.286, // …baseline
  numSize: 0.048, // …font size (× ticket height), Ancizar regular
  statCentersX: [0.188, 0.332, 0.506, 0.66, 0.809], // 5 cell centers = measured label centers
  statBaseY: 0.729, // stat-number baseline
  statSize: 0.05, // stat-number font size (× ticket height), Ancizar bold
};

// Format the five stat cells from raw values (Points gets thousands separators; best batch ×n).
export function statStrings(points: number, resolved: number, breaches: number, bestBatch: number): string[] {
  const csat = Math.min(99, Math.round(62 + points / 25 - breaches * 4));
  return [
    points.toLocaleString('en-US'),
    String(resolved),
    resolved ? (ROUND / resolved).toFixed(1) + 's' : '—',
    csat + '%',
    '×' + bestBatch,
  ];
}

export interface TicketData {
  variant: TicketVariant;
  no: string; // "000247" or the "——————" fallback
  stats: string[]; // five preformatted cell strings
}

// Draw the ticket at the given target width (native width if omitted). Returns a canvas so the
// caller can display it or export it (2× for download) without a data-URL round trip.
export async function renderResultTicket(data: TicketData, targetWidth?: number): Promise<HTMLCanvasElement> {
  const img = await loadTicket(data.variant);
  const W = targetWidth ?? img.naturalWidth;
  const H = W * (img.naturalHeight / img.naturalWidth);
  const c = document.createElement('canvas');
  c.width = Math.round(W);
  c.height = Math.round(H);
  const x = c.getContext('2d')!;
  x.drawImage(img, 0, 0, W, H);
  x.fillStyle = '#000';
  x.textBaseline = 'alphabetic';

  // ticket number — Ancizar regular, left-aligned right of "No."
  x.font = `400 ${L.numSize * H}px 'Ancizar Serif', Georgia, serif`;
  x.textAlign = 'left';
  x.fillText(data.no, L.numLeftX * W, L.numBaseY * H);

  // five stat numbers — Ancizar bold, centered on each baked label
  x.font = `700 ${L.statSize * H}px 'Ancizar Serif', Georgia, serif`;
  x.textAlign = 'center';
  for (let i = 0; i < 5 && i < data.stats.length; i++) {
    x.fillText(data.stats[i], L.statCentersX[i] * W, L.statBaseY * H);
  }
  return c;
}
